import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { ActionModal, Button, Modal } from '@ui/components/core'
import { MenuItem, TableList } from '@ui/components/elements/TableList/TableList'
import { Spinner } from '@ui/components/loaders'
import { Member, Role } from '@ui/interfaces'
import { useAuth } from '@ui/providers/AuthProvider'
import { useSettings } from '@ui/providers/SettingsProvider'
import { orgService } from '@ui/services'
import { CreateMemberInput } from '@ui/services'

import { InviteUserForm } from './components/forms/InviteUserForm'

export const Users = () => {
  const auth = useAuth()
  const { currentOrganization, updateRole, currentMember } = auth
  const orgId = currentOrganization!.id

  const { orgMembers, setOrgMembers } = useSettings()

  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const [modalState, setModalState] = useState(false)
  const [loading, setLoading] = useState(false)
  const { mutateAsync: inviteMember, isPending: isInvitingMember } = useMutation({
    mutationFn: (data: CreateMemberInput) => orgService.createMember(orgId.toString(), data),
    onSuccess: (member) => {
      setOrgMembers([...orgMembers, member])
      setModalState(false)
    },
    onError: (error) => {
      console.error('Failed to create member', error)
    },
  })

  const { mutateAsync: removeMember, isPending: isRemovingMember } = useMutation({
    mutationFn: (memberId: number) => orgService.removeMember(orgId.toString(), memberId),
    onSuccess: () => {
      setOrgMembers(orgMembers.filter((member) => member.id !== selectedMember?.id))
      setDeleteConfirmationModal(false)
    },
    onError: (error) => {
      console.error('Failed to remove member', error)
    },
  })

  const handleRemoveMember = async (memberId: string) => {
    setSelectedMember(orgMembers.find((member) => member.id.toString() === memberId) || null)
    await removeMember(parseInt(memberId))
  }

  const handleToggleRole = async (memberId: string) => {
    const member = orgMembers.find((member) => member.id.toString() === memberId)
    if (member) {
      const updatedMember = await updateRole(member.id, orgId, member.role !== 'admin' ? Role.ADMIN : Role.STANDARD)
      setOrgMembers(orgMembers.map((member) => (member.id === updatedMember.id ? updatedMember : member)))
    }
  }

  const menuItems: MenuItem[] = [
    {
      label: 'Delete',
      onClick: handleRemoveMember,
    },
  ]

  if (currentMember?.role === Role.ADMIN) {
    menuItems.push({
      label: 'Toggle Role',
      onClick: handleToggleRole,
    })
  }

  useEffect(() => {
    setLoading(isInvitingMember || isRemovingMember)
  }, [isInvitingMember, isRemovingMember])

  return (
    <div className="flex size-full flex-col space-y-4">
      <div className="flex flex-row items-center justify-between space-x-2">
        <div className="flex flex-col space-y-1">
          <h1 className="text-xl font-bold">Users</h1>
          <p className="text-sm text-gray-500">Invite users to your organization.</p>
        </div>
        <Button onClick={() => setModalState(true)}>Invite</Button>
      </div>

      <ActionModal
        open={deleteConfirmationModal}
        title="Delete Member"
        message="Are you sure you want to remove this member?"
        handleAction={() => {
          orgService.removeMember(orgId.toString(), selectedMember?.id || 0)
          setDeleteConfirmationModal(false)
        }}
        actionIdentifier="Remove"
        dismissAction={() => {
          setDeleteConfirmationModal(false)
        }}
        dismissIdentifier="Cancel"
      />
      <Modal title="Invite user" isOpen={modalState} onClose={() => setModalState(false)}>
        <InviteUserForm isLoading={loading} onSubmit={inviteMember} onClose={() => setModalState(false)} />
      </Modal>

      {isInvitingMember || isRemovingMember ? (
        <div className="flex h-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="w-[calc(100vw-19rem)]">
          <TableList
            items={orgMembers.map((member) => ({
              id: member.id.toString(),
              email: member.email,
              first_name: member.first_name,
              last_name: member.last_name,
              role: member.role,
            }))}
            menuItems={menuItems}
          />
        </div>
      )}
    </div>
  )
}
